import { resetSelection } from 'owa-mail-actions/lib/mailListSelectionActions';
import { singleSelectRow } from 'owa-mail-actions/lib/singleSelectRow';
import { initTableSelectionOnLoad } from 'owa-mail-actions/lib/initTableSelectionOnLoad';
import type { TableView } from 'owa-mail-list-store';
import { MailRowDataPropertyGetter } from 'owa-mail-list-store';
import shouldFirstRowBeSelectedOnLoad from 'owa-mail-list-store/lib/selectors/shouldFirstRowBeSelectedOnLoad';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import { orchestrator } from 'satcheljs';
import setSelectionAnchorRowKey from '../internal/mutators/setSelectionAnchorRowKey';
import setMultiSelectionAnchorRowKey from '../internal/mutators/setMultiSelectionAnchorRowKey';
import setFocusedRowKey from '../internal/mutators/setFocusedRowKey';
import { isFeatureEnabled } from 'owa-feature-flags';
import { setLastAutoFocusedRowKey } from '../mutators/setLastAutoFocusedRowKey';
import { shouldTableSortByRenewTime } from 'owa-mail-triage-common';

/**
 * Auto selects the first appropriate row in table
 * @param tableView in which to select the first row
 */
function autoSelectFirstRow(tableView: TableView) {
    const tableRowKeys = tableView.rowKeys;
    const tableRowsLength = tableRowKeys.length;

    if (tableRowsLength > 0) {
        let i = 0;
        let rowToSelect = tableRowKeys[i];

        // Auto select first row
        // which is unread or else first unpinned row
        while (i < tableRowsLength) {
            const rowKey = tableRowKeys[i];

            const isRowPinned =
                shouldTableSortByRenewTime(tableView.tableQuery) &&
                MailRowDataPropertyGetter.getIsPinned(rowKey, tableView);
            const unreadCount = MailRowDataPropertyGetter.getUnreadCount(rowKey, tableView);

            if (!isRowPinned || unreadCount > 0) {
                rowToSelect = rowKey;
                break;
            }

            i++;
        }

        // Select the row
        singleSelectRow(
            tableView,
            rowToSelect,
            false /* isUserNavigation */,
            MailListItemSelectionSource.Auto
        );
    } else if (isFeatureEnabled('tri-preserve-ml-selection') && isFeatureEnabled('tri-virtuoso')) {
        resetSelection(tableView, MailListItemSelectionSource.Reset, false /* isUserNavigation */);
    }
}

/**
 * Initializes selection on table load based on user's settings
 * @param tableView the table view
 */
const defaultExport: (
    actionMessage: ReturnType<typeof initTableSelectionOnLoad>
) => void | Promise<void> = orchestrator(initTableSelectionOnLoad, actionMessage => {
    const tableView = actionMessage.tableView;

    if (isFeatureEnabled('tri-mlFocusSelect')) {
        // When initializing selection, clear lastAutoFocusedRowKey so that we can focus the first row
        setLastAutoFocusedRowKey(tableView, '');
    }
    if (
        isFeatureEnabled('tri-preserve-ml-selection') &&
        isFeatureEnabled('tri-virtuoso') &&
        tableView.selectedRowKeys.size > 0
    ) {
        singleSelectRow(
            tableView,
            [...tableView.selectedRowKeys.keys()][0],
            false /* isUserNavigation */,
            MailListItemSelectionSource.Auto
        );
        return;
    }

    // Select first row if we should select first row on initial table load.
    // Note: We also want to select the first row on initial load when the user has jumped in the list regardless of the auto-select first row setting.
    // otherwise reset selection in table and set focus on first row
    if (shouldFirstRowBeSelectedOnLoad(tableView) || tableView.loadedStartIndex > 0) {
        autoSelectFirstRow(tableView);
    } else {
        resetSelection(
            tableView,
            MailListItemSelectionSource.Reset,
            actionMessage.isUserNavigation
        );

        if (tableView.rowKeys.length > 0) {
            setSelectionAnchorRowKey(tableView, tableView.rowKeys[0]);
            setMultiSelectionAnchorRowKey(tableView, tableView.rowKeys[0]);
            setFocusedRowKey(tableView, tableView.rowKeys[0]);
        }
    }
});

export default defaultExport;
